import { animate } from "motion";

export class VideoPlayer {
    constructor(container, options) {
        if (!container) return;

        this.container = container;
        this.video = container.querySelector(".player__video");
        this.control = container.querySelector(".player__control");
        this.buffer = container.querySelector(".player__progress__buffer");
        this.timeline = container.querySelector(".player__progress__timeline");
        this.volumeBars = container.querySelectorAll(".player__volume button");
        this.controls = container.querySelector(".player__controls");

        this.hideControlsTimeout = null;
        this.areControlsVisible = true;

        this.#init(options);
    }

    play() {
        this.video.play();
    }

    pause() {
        this.video.pause();
    }

    #init(options) {
        const { controls } = options ?? {};
        this.control.addEventListener("click", () => this.#togglePlay());
        this.video.addEventListener("loadedmetadata", () => this.#onLoadedMetadata());

        if (this.video.readyState >= 2) {
            this.#onLoadedMetadata();
        }

        this.video.addEventListener("progress", () => this.#updateBuffer());
        this.video.addEventListener("play", () => this.#onPlay());
        this.video.addEventListener("pause", () => this.#onPause());
        this.video.addEventListener("timeupdate", () => this.#updateTimeline());
        this.timeline.addEventListener("input", () => this.#seek());

        this.volumeBars.forEach((bar) => {
            bar.addEventListener("click", () => this.#setVolume(bar));
        });

        if (controls && controls?.position) {
            this.container.classList.add(`player--controls-${controls.position}`);
        }

        // Auto-hide controls functionality
        this.#setupAutoHideControls();
    }

    #setupAutoHideControls() {
        const handleMouseMove = () => {
            this.#showControls();
            this.#resetHideTimer();
        };

        const handleMouseLeave = () => {
            this.#resetHideTimer();
        };

        // Add mouse event listeners to the container
        this.container.addEventListener("mousemove", handleMouseMove);
        this.container.addEventListener("mouseleave", handleMouseLeave);

        // Start with controls visible and begin hide timer
        this.#resetHideTimer();
    }

    #showControls() {
        if (!this.areControlsVisible) {
            this.areControlsVisible = true;
            animate(
                this.controls,
                {
                    opacity: 1,
                    y: 0,
                },
                {
                    duration: 0.4,
                    easing: "ease-out",
                }
            );
        }
    }

    #hideControls() {
        if (this.areControlsVisible) {
            this.areControlsVisible = false;
            animate(
                this.controls,
                {
                    opacity: 0,
                    y: 20,
                },
                {
                    duration: 0.4,
                    easing: "ease-out",
                }
            );
        }
    }

    #resetHideTimer() {
        clearTimeout(this.hideControlsTimeout);
        this.hideControlsTimeout = setTimeout(() => {
            this.#hideControls();
        }, 5000);
    }

    #togglePlay() {
        if (this.video.paused) {
            this.play();
        } else {
            this.pause();
        }
    }

    #onLoadedMetadata() {
        this.timeline.max = this.video.duration;

        const hasAudio =
            this.video.mozHasAudio ||
            this.video.webkitAudioDecodedByteCount > 0 ||
            (this.video.audioTracks && this.video.audioTracks.length > 0);

        if (!hasAudio) {
            this.volumeBars.forEach((bar) => {
                bar.classList.remove("active");
                bar.setAttribute("disabled", true);
            });
        }
    }

    #updateBuffer() {
        if (this.video.buffered.length > 0) {
            const bufferedEnd = this.video.buffered.end(this.video.buffered.length - 1);
            const duration = this.video.duration;
            if (duration > 0) {
                const percent = (bufferedEnd / duration) * 100;
                this.buffer.value = percent;
            }
        }
    }

    #onPlay() {
        this.control.setAttribute("aria-label", this.control.getAttribute("data-play-label"));
        this.control.classList.add("player__control--pressed");
        // Reset hide timer when playing
        this.#resetHideTimer();
    }

    #onPause() {
        this.control.setAttribute("aria-label", this.control.getAttribute("data-pause-label"));
        this.control.classList.remove("player__control--pressed");
        // Show controls when paused and clear timer
        this.#showControls();
        clearTimeout(this.hideControlsTimeout);
    }

    #updateTimeline() {
        const currentTime = this.video.currentTime;
        const duration = this.video.duration;

        if (duration > 0) {
            const percent = (currentTime / duration) * 100;
            this.timeline.style.setProperty("--value", `${percent}%`);
        }

        this.timeline.value = currentTime;
    }

    #seek() {
        this.video.currentTime = this.timeline.value;
        this.#updateTimeline();
    }

    #setVolume(bar) {
        const level = parseFloat(bar.dataset.level);
        this.video.volume = level;
        this.video.muted = false;
        this.volumeBars.forEach((b) => {
            b.classList.toggle("active", parseFloat(b.dataset.level) <= level);
        });
    }
}
